import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, ArrowRight, Check, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import SuburbAutocomplete from '../components/SuburbAutocomplete';

const STEPS = [
  { id: 1, title: "What customers do you want?", subtitle: "Select the service you provide" },
  { id: 2, title: "Where can you service them?", subtitle: "Choose your coverage area" },
  { id: 3, title: "What size opportunities?", subtitle: "Set minimum/maximum values" },
  { id: 4, title: "What makes a lead suitable?", subtitle: "Qualification criteria" },
  { id: 5, title: "How many leads?", subtitle: "Volume and pricing" },
  { id: 6, title: "Review & Save", subtitle: "Confirm your profile" },
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function DemandProfileWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Categories and services
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceConfig, setServiceConfig] = useState(null);
  
  // Step 1: Service
  const [profileName, setProfileName] = useState("");
  
  // Step 2: Geography
  const [coverageType, setCoverageType] = useState("states"); // "states" | "national" | "advanced"
  const [selectedStates, setSelectedStates] = useState([]);
  const [geographyRules, setGeographyRules] = useState([]); // typed rules for "advanced"
  const [locationPresets, setLocationPresets] = useState([]);
  
  // Step 3: Opportunity Value
  const [valueSettings, setValueSettings] = useState({});
  // Structure: {metric_id: {mode: "any"|"minimum"|"range", min_dollars, max_dollars}}
  
  // Step 4: Qualification
  const [qualificationAnswers, setQualificationAnswers] = useState({});
  
  // Step 5: Volume & Pricing
  const [monthlyVolume, setMonthlyVolume] = useState(10);
  const [targetCPL, setTargetCPL] = useState(50);
  const [maxCPL, setMaxCPL] = useState(100);
  const [exclusivity, setExclusivity] = useState("shared");
  
  useEffect(() => {
    fetchCategories();
    fetchLocationPresets();
  }, []);
  
  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
      toast.error('Failed to load categories');
    }
  };

  const fetchLocationPresets = async () => {
    try {
      const res = await api.get('/framework/location-rules');
      setLocationPresets(res.data.rules || []);
    } catch (err) {
      // Non-fatal — presets are optional
    }
  };
  
  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setSelectedService(null);
    setServiceConfig(null);
  };
  
  const handleServiceSelect = async (serviceId) => {
    setSelectedService(serviceId);
    // Fetch service config
    try {
      const res = await api.get(`/categories/${selectedCategory.category_id}/services/${serviceId}`);
      setServiceConfig(res.data);
      
      // Initialize value settings with primary metric
      const primaryMetric = res.data.value_metric;
      if (primaryMetric) {
        setValueSettings({
          [primaryMetric]: { mode: "minimum", min_dollars: null, max_dollars: null }
        });
      }
    } catch (err) {
      console.error('Failed to load service config:', err);
    }
  };
  
  const handleNext = () => {
    // Validation
    if (step === 1 && (!selectedCategory || !selectedService || !profileName.trim())) {
      toast.error("Please select a service and enter a profile name");
      return;
    }
    if (step === 2 && coverageType === "states" && selectedStates.length === 0) {
      toast.error("Please select at least one state");
      return;
    }
    if (step === 2 && coverageType === "advanced" && geographyRules.length === 0) {
      toast.error("Add at least one geography rule");
      return;
    }
    if (step === 5 && (!monthlyVolume || !maxCPL)) {
      toast.error("Please enter volume and pricing");
      return;
    }
    
    if (step < 6) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };
  
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build value_requirements
      const value_requirements = {};
      Object.entries(valueSettings).forEach(([metric_id, settings]) => {
        if (settings.mode !== "any") {
          value_requirements[metric_id] = {
            min_dollars: settings.min_dollars,
            max_dollars: settings.max_dollars,
            currency: "AUD",
            allow_estimated: true,
            allow_unknown_view: false,
            allow_unknown_purchase: false,
          };
        }
      });
      
      // For legacy `geography` (min_length=1), always include at least the
      // resolved state list or a placeholder so backend validation passes.
      const advancedStates = Array.from(new Set(geographyRules.filter(r => r.type === 'state').map(r => r.state).filter(Boolean)));
      const geographyList = coverageType === "national"
        ? ["National"]
        : coverageType === "advanced"
          ? (advancedStates.length ? advancedStates : ["Advanced"])
          : selectedStates;

      const payload = {
        vertical: selectedCategory.label,
        category: selectedCategory.label, // Legacy
        service_id: selectedService,
        category_id: selectedCategory.category_id,
        geography: geographyList,
        geography_rules: coverageType === "advanced"
                          ? geographyRules
                          : coverageType === "national"
                            ? [{ type: "national", label: "Australia-wide" }]
                            : null,
        value_requirements: Object.keys(value_requirements).length > 0 ? value_requirements : null,
        monthly_volume: monthlyVolume,
        target_cpl: targetCPL,
        max_cpl: maxCPL,
        required_fields: [],
        qualification_criteria: JSON.stringify(qualificationAnswers),
        exclusivity,
        max_lead_age_hours: 72,
        verification_required: [],
        delivery_method: ["dashboard"],
        contact_automation: [],
        monthly_budget: maxCPL * monthlyVolume,
        notes: profileName,
      };
      
      await api.post('/demand-profiles', payload);
      toast.success("Demand profile created!");
      navigate('/buyer/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };
  
  const renderStep = () => {
    switch (step) {
      case 1:
        return renderStepService();
      case 2:
        return renderStepGeography();
      case 3:
        return renderStepValue();
      case 4:
        return renderStepQualification();
      case 5:
        return renderStepVolume();
      case 6:
        return renderStepReview();
      default:
        return null;
    }
  };
  
  const renderStepService = () => (
    <div className="space-y-6">
      <div>
        <Label>Profile Name</Label>
        <Input
          value={profileName}
          onChange={(e) => setProfileName(e.target.value)}
          placeholder="e.g., Equipment Finance - National"
          className="mt-2"
          data-testid="profile-name-input"
        />
      </div>
      
      <div>
        <Label className="text-base font-semibold mb-3 block">Select Category</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {categories.map(cat => (
            <Card
              key={cat.category_id}
              className={`cursor-pointer hover:border-[#0055FF] transition-colors ${selectedCategory?.category_id === cat.category_id ? 'border-[#0055FF] bg-blue-50' : ''}`}
              onClick={() => handleCategorySelect(cat)}
              data-testid={`category-${cat.category_id}`}
            >
              <CardContent className="p-4">
                <div className="font-semibold text-sm">{cat.label}</div>
                <div className="text-xs text-gray-600 mt-1">{Object.keys(cat.services).length} services</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      
      {selectedCategory && (
        <div>
          <Label className="text-base font-semibold mb-3 block">Select Service</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(selectedCategory.services).map(([serviceId, service]) => (
              <Card
                key={serviceId}
                className={`cursor-pointer hover:border-[#0055FF] transition-colors ${selectedService === serviceId ? 'border-[#0055FF] bg-blue-50' : ''}`}
                onClick={() => handleServiceSelect(serviceId)}
                data-testid={`service-${serviceId}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{service.label}</div>
                    {selectedService === serviceId && <Check className="w-4 h-4 text-[#0055FF]" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  
  const renderStepGeography = () => (
    <div className="space-y-6" data-testid="wizard-step-geography">
      <RadioGroup value={coverageType} onValueChange={setCoverageType}>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="national" id="national" data-testid="coverage-national" />
          <Label htmlFor="national" className="cursor-pointer">Australia-wide</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="states" id="states" data-testid="coverage-states" />
          <Label htmlFor="states" className="cursor-pointer">Selected states/territories</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="advanced" id="advanced" data-testid="coverage-advanced" />
          <Label htmlFor="advanced" className="cursor-pointer">Advanced rules (postcodes / radius)</Label>
        </div>
      </RadioGroup>

      {coverageType === "states" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {STATES.map(state => (
            <div key={state} className="flex items-center space-x-2">
              <Checkbox
                id={state}
                checked={selectedStates.includes(state)}
                onCheckedChange={(checked) => {
                  if (checked) setSelectedStates([...selectedStates, state]);
                  else         setSelectedStates(selectedStates.filter(s => s !== state));
                }}
                data-testid={`coverage-state-${state}`}
              />
              <Label htmlFor={state} className="cursor-pointer">{state}</Label>
            </div>
          ))}
        </div>
      )}

      {coverageType === "advanced" && (
        <AdvancedGeographyEditor
          rules={geographyRules}
          setRules={setGeographyRules}
          presets={locationPresets}
          onPresetSaved={(preset) => setLocationPresets([preset, ...locationPresets])}
        />
      )}
    </div>
  );
  
  const renderStepValue = () => {
    if (!serviceConfig || !serviceConfig.value_metric_config) {
      return <div className="text-gray-600">No value requirements for this service</div>;
    }
    
    const metricConfig = serviceConfig.value_metric_config;
    const primaryMetric = serviceConfig.value_metric;
    const valueSetting = valueSettings[primaryMetric] || { mode: "minimum" };
    
    return (
      <div className="space-y-6">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm text-blue-900">
            <strong>What this means:</strong> This is the value of the customer's {metricConfig.label.toLowerCase()}.
            Your price per lead is set separately in the next step.
          </div>
        </div>
        
        <div>
          <Label className="text-base font-semibold mb-3 block">{metricConfig.label}</Label>
          
          <RadioGroup
            value={valueSetting.mode}
            onValueChange={(mode) => setValueSettings({ ...valueSettings, [primaryMetric]: { ...valueSetting, mode } })}
          >
            <div className="flex items-center space-x-2 mb-2">
              <RadioGroupItem value="any" id="any" />
              <Label htmlFor="any" className="cursor-pointer">Any value / No minimum</Label>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <RadioGroupItem value="minimum" id="minimum" />
              <Label htmlFor="minimum" className="cursor-pointer">Set a minimum</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="range" id="range" />
              <Label htmlFor="range" className="cursor-pointer">Set a range (min and max)</Label>
            </div>
          </RadioGroup>
        </div>
        
        {valueSetting.mode !== "any" && (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Suggested Minimums (select or enter custom)</Label>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {metricConfig.suggested_minimums?.map(amt => (
                <Button
                  key={amt}
                  variant="outline"
                  size="sm"
                  onClick={() => setValueSettings({ ...valueSettings, [primaryMetric]: { ...valueSetting, min_dollars: amt } })}
                  className={valueSetting.min_dollars === amt ? 'border-[#0055FF] bg-blue-50' : ''}
                >
                  A${(amt / 1000)}k+
                </Button>
              ))}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Custom Minimum (A$)</Label>
                <Input
                  type="number"
                  value={valueSetting.min_dollars || ''}
                  onChange={(e) => setValueSettings({ ...valueSettings, [primaryMetric]: { ...valueSetting, min_dollars: parseInt(e.target.value) || null } })}
                  placeholder="e.g., 20000"
                  className="mt-2"
                  data-testid="min-value-input"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {valueSetting.min_dollars ? `A$${valueSetting.min_dollars.toLocaleString()} or more` : 'Enter whole dollars'}
                </div>
              </div>
              
              {valueSetting.mode === "range" && (
                <div>
                  <Label>Maximum (A$) - Optional</Label>
                  <Input
                    type="number"
                    value={valueSetting.max_dollars || ''}
                    onChange={(e) => setValueSettings({ ...valueSettings, [primaryMetric]: { ...valueSetting, max_dollars: parseInt(e.target.value) || null } })}
                    placeholder="e.g., 100000"
                    className="mt-2"
                    data-testid="max-value-input"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {valueSetting.max_dollars ? `Up to A$${valueSetting.max_dollars.toLocaleString()}` : 'Leave blank for no maximum'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  const renderStepQualification = () => (
    <div className="space-y-6">
      <div className="text-sm text-gray-600">
        Additional qualification criteria will be configured based on your service.
        For now, we'll use default settings.
      </div>
    </div>
  );
  
  const renderStepVolume = () => (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-sm text-blue-900">
          <strong>Important:</strong> Lead volume and pricing are separate from the opportunity value you set earlier.
        </div>
      </div>
      
      <div>
        <Label>Target Leads Per Month</Label>
        <Input
          type="number"
          value={monthlyVolume}
          onChange={(e) => setMonthlyVolume(parseInt(e.target.value) || 0)}
          className="mt-2"
          data-testid="monthly-volume-input"
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Target Price Per Lead (A$)</Label>
          <Input
            type="number"
            value={targetCPL}
            onChange={(e) => setTargetCPL(parseFloat(e.target.value) || 0)}
            className="mt-2"
          />
        </div>
        <div>
          <Label>Maximum Price Per Lead (A$)</Label>
          <Input
            type="number"
            value={maxCPL}
            onChange={(e) => setMaxCPL(parseFloat(e.target.value) || 0)}
            className="mt-2"
            data-testid="max-cpl-input"
          />
        </div>
      </div>
      
      <div>
        <Label>Lead Sharing Preference</Label>
        <RadioGroup value={exclusivity} onValueChange={setExclusivity} className="mt-2">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="exclusive" id="exclusive" />
            <Label htmlFor="exclusive" className="cursor-pointer">Exclusive leads only</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="shared" id="shared" />
            <Label htmlFor="shared" className="cursor-pointer">Shared leads acceptable</Label>
          </div>
        </RadioGroup>
      </div>
      
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-semibold text-gray-900">Estimated Monthly Budget</div>
        <div className="text-2xl font-bold text-[#0055FF] mt-1">
          A${(maxCPL * monthlyVolume).toLocaleString()}
        </div>
        <div className="text-xs text-gray-600 mt-1">
          Based on {monthlyVolume} leads × A${maxCPL} maximum
        </div>
      </div>
    </div>
  );
  
  const renderStepReview = () => {
    const primaryMetric = serviceConfig?.value_metric;
    const valueSetting = valueSettings[primaryMetric];
    const metricLabel = serviceConfig?.value_metric_config?.label || "Value";
    
    let valueSummary = "Any value accepted";
    if (valueSetting?.mode === "minimum" && valueSetting.min_dollars) {
      valueSummary = `${metricLabel}: A$${valueSetting.min_dollars.toLocaleString()} or more`;
    } else if (valueSetting?.mode === "range") {
      const minStr = valueSetting.min_dollars ? `A$${valueSetting.min_dollars.toLocaleString()}` : "any";
      const maxStr = valueSetting.max_dollars ? `A$${valueSetting.max_dollars.toLocaleString()}` : "unlimited";
      valueSummary = `${metricLabel}: ${minStr} to ${maxStr}`;
    }
    
    const geographySummary = coverageType === "national"
      ? "Australia-wide"
      : coverageType === "advanced"
        ? `${geographyRules.length} rule${geographyRules.length !== 1 ? 's' : ''} · ${geographyRules.map(r => r.type).join(', ')}`
        : selectedStates.join(", ");
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Profile Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-semibold text-gray-700">Profile Name</div>
              <div className="text-base text-gray-900">{profileName}</div>
            </div>
            
            <div>
              <div className="text-sm font-semibold text-gray-700">Service</div>
              <div className="text-base text-gray-900">
                {selectedCategory?.label} → {serviceConfig?.label}
              </div>
            </div>
            
            <div>
              <div className="text-sm font-semibold text-gray-700">Coverage</div>
              <div className="text-base text-gray-900">{geographySummary}</div>
            </div>
            
            <div>
              <div className="text-sm font-semibold text-gray-700">Opportunity Size</div>
              <div className="text-base text-gray-900">{valueSummary}</div>
            </div>
            
            <div>
              <div className="text-sm font-semibold text-gray-700">Volume & Pricing</div>
              <div className="text-base text-gray-900">
                {monthlyVolume} leads/month at up to A${maxCPL} per lead
              </div>
              <div className="text-sm text-gray-600">
                Estimated monthly budget: A${(maxCPL * monthlyVolume).toLocaleString()}
              </div>
            </div>
            
            <div>
              <div className="text-sm font-semibold text-gray-700">Exclusivity</div>
              <div className="text-base text-gray-900 capitalize">{exclusivity}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.id}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${step >= s.id ? 'bg-[#0055FF] text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {step > s.id ? <Check className="w-4 h-4" /> : s.id}
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${step > s.id ? 'bg-[#0055FF]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center mt-4">
            <div className="text-sm text-gray-600">Step {step} of {STEPS.length}</div>
          </div>
        </div>
        
        {/* Current Step */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">{STEPS[step - 1].title}</CardTitle>
            <CardDescription>{STEPS[step - 1].subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={loading}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="next-btn"
          >
            {loading ? 'Saving...' : step === 6 ? 'Create Profile' : 'Next'}
            {step < 6 && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Advanced geography rule editor — used inside the wizard's geography step
// ---------------------------------------------------------------------------

const RULE_TYPES = [
  { value: 'national',        label: 'National coverage' },
  { value: 'state',           label: 'State' },
  { value: 'postcode_list',   label: 'Postcode list' },
  { value: 'postcode_prefix', label: 'Postcode prefix' },
  { value: 'radius',          label: 'Radius from postcode' },
];

const AdvancedGeographyEditor = ({ rules, setRules, presets, onPresetSaved }) => {
  const [presetId, setPresetId] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const addRule = () => setRules([...rules, { type: 'state', state: 'NSW' }]);
  const updateRule = (i, patch) => setRules(rules.map((r, ix) => ix === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setRules(rules.filter((_, ix) => ix !== i));
  const applyPreset = () => {
    const preset = (presets || []).find(p => p.rule_id === presetId);
    if (!preset) return;
    setRules([...rules, ...preset.rules.map(r => ({ ...r, label: r.label || preset.label }))]);
    setPresetId('');
  };

  const saveAsPreset = async () => {
    if (!newPresetName.trim()) {
      toast.error("Give the preset a name");
      return;
    }
    if (!rules.length) {
      toast.error("Add at least one rule first");
      return;
    }
    setSavingPreset(true);
    try {
      const res = await api.post('/framework/location-rules', {
        label: newPresetName.trim(),
        rules,
      });
      toast.success(`Preset "${res.data.label}" saved to your library`);
      setNewPresetName('');
      setShowSaveInput(false);
      if (onPresetSaved) onPresetSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4" data-testid="advanced-geography-editor">
      {(presets || []).length > 0 && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1">Attach preset</label>
            <select
              value={presetId}
              onChange={(e) => setPresetId(e.target.value)}
              className="w-full h-10 rounded border border-gray-300 bg-white text-sm px-3"
              data-testid="wizard-preset-select"
            >
              <option value="">— Select a preset —</option>
              {presets.map(p => (
                <option key={p.rule_id} value={p.rule_id}>
                  {p.kind === 'personal' ? '★ ' : ''}{p.label} ({p.rules.length} rule{p.rules.length !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={applyPreset}
            disabled={!presetId}
            className="h-10 border-gray-300"
            data-testid="wizard-preset-apply"
          >
            Attach
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {rules.map((r, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded p-3" data-testid={`wizard-rule-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <select
                value={r.type}
                onChange={(e) => updateRule(i, { type: e.target.value })}
                className="h-9 rounded border border-gray-300 text-sm px-2 bg-white"
                data-testid={`wizard-rule-type-${i}`}
              >
                {RULE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
              </select>
              <button
                onClick={() => removeRule(i)}
                className="text-xs text-red-600 hover:underline ml-auto"
                data-testid={`wizard-rule-remove-${i}`}
              >
                Remove
              </button>
            </div>
            <WizardRuleFields rule={r} update={(p) => updateRule(i, p)} idx={i} />
          </div>
        ))}
        {rules.length === 0 && (
          <div className="text-xs text-gray-500 italic">No rules yet — attach a preset above or add one below.</div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          variant="outline"
          onClick={addRule}
          className="h-9 border-gray-300"
          data-testid="wizard-rule-add"
        >
          + Add rule
        </Button>
        {rules.length > 0 && !showSaveInput && (
          <Button
            variant="outline"
            onClick={() => setShowSaveInput(true)}
            className="h-9 border-gray-300 ml-auto"
            data-testid="wizard-save-preset-toggle"
          >
            Save as personal preset
          </Button>
        )}
      </div>

      {showSaveInput && (
        <div className="border-t border-gray-200 pt-4 flex flex-wrap items-end gap-2" data-testid="wizard-save-preset-form">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1">Preset name</label>
            <Input
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="e.g. My Sydney service area"
              data-testid="wizard-save-preset-name"
            />
          </div>
          <Button
            onClick={saveAsPreset}
            disabled={savingPreset}
            className="h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white"
            data-testid="wizard-save-preset-btn"
          >
            {savingPreset ? 'Saving…' : 'Save preset'}
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowSaveInput(false); setNewPresetName(''); }}
            className="h-10 border-gray-300"
            data-testid="wizard-save-preset-cancel"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

const WizardRuleFields = ({ rule, update, idx }) => {
  if (rule.type === 'national')
    return <div className="text-xs text-gray-600">Matches any Australian lead.</div>;

  if (rule.type === 'state')
    return (
      <select
        value={rule.state || 'NSW'}
        onChange={(e) => update({ state: e.target.value })}
        className="h-9 rounded border border-gray-300 text-sm px-2 bg-white w-40"
        data-testid={`wizard-rule-state-${idx}`}
      >
        {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    );

  if (rule.type === 'postcode_list')
    return (
      <Input
        placeholder="2000, 2010, 2088"
        value={(rule.postcodes || []).join(', ')}
        onChange={(e) => update({ postcodes: parseCsv(e.target.value) })}
        data-testid={`wizard-rule-postcodes-${idx}`}
      />
    );

  if (rule.type === 'postcode_prefix')
    return (
      <Input
        placeholder="20, 21, 22"
        value={(rule.prefixes || []).join(', ')}
        onChange={(e) => update({ prefixes: parseCsv(e.target.value) })}
        data-testid={`wizard-rule-prefixes-${idx}`}
      />
    );

  if (rule.type === 'radius')
    return (
      <div className="space-y-2">
        <div className="text-[11px] text-gray-500">Type a suburb to auto-fill the postcode, or enter one directly.</div>
        <div className="grid grid-cols-2 gap-2">
          <SuburbAutocomplete
            value={rule.center_label || ''}
            onChange={(v) => update({ center_label: v })}
            onSelect={(item) => update({
              center_label: `${item.suburb}, ${item.state}`,
              center_postcode: item.postcode,
              center_lat: item.lat,
              center_lng: item.lng,
            })}
            testid={`wizard-rule-suburb-${idx}`}
          />
          <Input
            placeholder="Postcode"
            value={rule.center_postcode || ''}
            onChange={(e) => update({ center_postcode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            inputMode="numeric"
            maxLength={4}
            data-testid={`wizard-rule-center-${idx}`}
          />
        </div>
        <Input
          type="number"
          placeholder="Radius km (e.g. 50)"
          value={rule.radius_km || ''}
          onChange={(e) => update({ radius_km: Number(e.target.value) || 0 })}
          data-testid={`wizard-rule-radius-${idx}`}
        />
      </div>
    );

  return null;
};

const parseCsv = (raw) => (raw || '').split(/[,\s]+/).map(x => x.trim()).filter(Boolean);
